'use client'
// Components
import { Box, Typography } from '@/CommonComponents'
import type { ISeverity } from '@/types/severity'
import type { IFinding } from '@/types/appsec/finding'
// Hooks
import { useState } from 'react'
import { useTexts } from '@/contexts/LanguageContext'
// Constants
import { COLORS } from '@/lib/constants/colors'
import { SEVERITIES as SEVERITIES_CONSTANTS } from '@/lib/constants/severities'
import { getOccurrences } from './utils'
interface ISeverityFilterButtonProps {
  severity: ISeverity
  repository?: string | null
  findings: IFinding[] // TODO: Remove this prop
  isSelected: boolean
  onClick: () => void
}
interface IFilterButtonStylesProps {
  isSelected: boolean
  color?: string
  backgroundColor?: string
  clickable?: boolean
  borderColor?: string
}
function getFilterButtonStyles({
  isSelected,
  color,
  backgroundColor,
  clickable = true,
  borderColor,
}: IFilterButtonStylesProps) {
  return {
    cursor: clickable ? 'pointer' : 'default',
    padding: 1,
    border: '1px solid',
    borderColor: isSelected ? borderColor || color : 'transparent',
    borderRadius: 1,
    backgroundColor: backgroundColor,
    color: color,
    minWidth: 100,
    minHeight: 70,
  }
}

function SeverityFilterButton({ severity, repository, findings, isSelected, onClick }: ISeverityFilterButtonProps) {
  const { SEVERITIES } = useTexts()
  const BUTTON_COLORS =
    severity in SEVERITIES ? COLORS.SEVERITY[severity] : { backgroundColor: 'transparent', color: 'inherit' }
  return (
    <Box
      onClick={onClick}
      sx={getFilterButtonStyles({
        isSelected,
        color: BUTTON_COLORS.color,
        backgroundColor: BUTTON_COLORS.backgroundColor,
        clickable: true,
      })}
    >
      <Typography bold>{SEVERITIES[severity]}</Typography>
      <Typography sx={{ fontFamily: 'monospace' }}>
        {getOccurrences({
          findings,
          severity,
          status: null,
          repository,
        })}
      </Typography>
    </Box>
  )
}

interface ISeverityFilterProps<T extends IFinding> {
  findings: T[]
  filterCriteria: {
    severity: ISeverity | null
    repository?: string | null
  }
  onChange: (severity: ISeverity | null) => void
}

export default function SeverityFilter<T extends IFinding>({
  findings,
  filterCriteria,
  onChange,
}: ISeverityFilterProps<T>) {
  const { ALL } = useTexts()
  const [selectedSeverity, setSelectedSeverity] = useState<ISeverity | null>(filterCriteria.severity || null)
  const SEVERITY_OPTIONS = Object.values(SEVERITIES_CONSTANTS)
  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, width: '100%' }}>
        <Box
          sx={{
            padding: 1,
            border: '1px solid',
            borderColor: selectedSeverity === null ? COLORS.GREY.DARK : COLORS.BORDER.DEFAULT,
            borderRadius: 1,
            backgroundColor: 'transparent',
            color: 'inherit',
            minWidth: 100,
            minHeight: 70,
            cursor: 'pointer',
          }}
          onClick={() => {
            setSelectedSeverity(null)
            onChange(null)
          }}
        >
          <Typography bold>{ALL}</Typography>
          <Typography>
            {getOccurrences({
              findings,
              severity: null,
              status: null,
              repository: filterCriteria.repository || null,
            })}
          </Typography>
        </Box>
        {SEVERITY_OPTIONS.map((severity) => (
          <SeverityFilterButton
            key={severity}
            severity={severity}
            findings={findings}
            repository={filterCriteria.repository || null}
            isSelected={selectedSeverity === severity}
            onClick={() => {
              if (selectedSeverity === severity) {
                setSelectedSeverity(null)
                onChange(null)
              } else {
                setSelectedSeverity(severity)
                onChange(severity)
              }
            }}
          />
        ))}
      </Box>
    </>
  )
}
